import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { useIntl, FormattedMessage } from "react-intl";
import { useSnackbar } from "notistack";
import { Box, Typography, Chip, CircularProgress } from "@mui/material";
import {
  MapPin, Clock, Calendar, Bookmark, Send,
  CheckCircle, ArrowLeft, Euro, Upload, FileText,
  Trash2, Paperclip, PenLine, ExternalLink,
} from "lucide-react";
import sh from "bundles/common/utils/sh";
import ProfilePicture from "bundles/common/components/ProfilePicture";
import HTML from "bundles/common/components/HTML";
import FileDropzone from "bundles/common/components/FileDropzone";

import { formatJobOfferLocations, incrementJobOfferMetric } from "bundles/common/utils/utils";
import { uploadResume, uploadDocument } from "bundles/common/utils/uploadDocument";
import { replaceCurrentCandidate } from "bundles/Candidate/constants/candidateConstants";
import { suppressNextProfileBoost } from "bundles/Candidate/hooks/useProfileCompletionBoost";
import { trackJobOfferApplication } from "bundles/common/utils/marketing";
import { getUtmParams } from "bundles/common/utils/utm";

const cardSx = {
  bgcolor: "hsl(0, 0%, 100%)",
  borderRadius: "16px",
  border: "1px solid hsla(160, 10%, 90%, 0.6)",
  p: "20px",
};

const sectionTitleSx = {
  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  fontSize: "14px",
  color: "hsl(178, 93%, 10%)",
  mb: "12px",
};

const infoPillSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  px: "12px",
  py: "6px",
  borderRadius: "12px",
  bgcolor: "hsla(160, 10%, 96%, 0.5)",
  fontSize: "12px",
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(178, 93%, 10%)",
  border: "1px solid hsla(160, 10%, 90%, 0.4)",
};

const skillChipSx = {
  display: "inline-flex",
  alignItems: "center",
  px: "12px",
  py: "4px",
  borderRadius: "9999px",
  bgcolor: "hsla(160, 10%, 96%, 0.5)",
  fontSize: "12px",
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(178, 93%, 10%)",
  border: "1px solid hsla(160, 10%, 90%, 0.4)",
};

const snackbarActionSx = {
  border: "none",
  bgcolor: "transparent",
  cursor: "pointer",
  color: "#fff",
  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  fontSize: "13px",
  px: "8px",
  py: "8px",
  textDecoration: "underline",
};

// Extra application documents: the default dropzone types plus images.
const EXTRA_DOCUMENT_ACCEPT = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
};

const JobDetailPanel = ({ jobOffer, updateJobOffer, onNavigate }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentCandidate = useSelector((state) => state.current_candidate);
  const railsEnv = useSelector((state) => state.railsEnv);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const dateLocale = { fr: "fr-BE", "fr-fr": "fr-FR", nl: "nl-BE", en: "en-GB" }[i18nLocale] || "fr-BE";

  const [candidateApplication, setCandidateApplication] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [coverLetter, setCoverLetter] = useState("");
  const [coverLetterMode, setCoverLetterMode] = useState("write");
  const [coverLetterFile, setCoverLetterFile] = useState(null);
  const [resume, setResume] = useState(undefined);
  const [initialFiles, setInitialFiles] = useState([]);
  const [extraFiles, setExtraFiles] = useState([]);
  const [showExtraDocs, setShowExtraDocs] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const clInputRef = useRef(null);

  useEffect(() => {
    if (jobOffer?.id && jobOffer?.job_applications?.length > 0) {
      const app = jobOffer.job_applications.find(
        (a) => String(a.candidate_id) === String(currentCandidate?.id)
      );
      setCandidateApplication(app || null);
    } else {
      setCandidateApplication(null);
    }
  }, [jobOffer?.id, jobOffer?.job_applications, currentCandidate?.id]);

  useEffect(() => {
    setIsApplying(false);
    setCoverLetter("");
    setCoverLetterMode("write");
    setCoverLetterFile(null);
    setExtraFiles([]);
    setShowExtraDocs(false);
    setErrors({});
    const files = [];
    if (currentCandidate?.resume_url) {
      files.push(currentCandidate.resume_url);
    }
    setInitialFiles(files);
    setResume(currentCandidate?.resume_url || undefined);
  }, [jobOffer?.id, currentCandidate?.resume_url]);

  // favorite_offers is a string[] column, job offer ids are numbers: compare as strings.
  const isFavorite = (currentCandidate?.favorite_offers || []).some(
    (id) => String(id) === String(jobOffer?.id)
  );

  const skills = jobOffer?.resource_skills || [];
  const hardSkills = skills.filter((s) => s.category === "hard");
  const softSkills = skills.filter((s) => s.category === "soft");
  const langSkills = skills.filter((s) => s.category === "lang");

  const company = jobOffer?.company;

  const handleFavorite = () => {
    const favorites = currentCandidate?.favorite_offers || [];
    const newFavorites = isFavorite
      ? favorites.filter((id) => String(id) !== String(jobOffer.id))
      : [...favorites, jobOffer.id];

    sh.put("update_favorite_offers", {
      authenticity_token: authenticityToken,
      favorite_offers: newFavorites,
    })
      .then(() => {
        replaceCurrentCandidate(dispatch, authenticityToken, () => {});
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(
          intl.formatMessage({ id: "candidate.dashboard.action_failed", defaultMessage: "Une erreur est survenue. Réessayez." }),
          { variant: "error" }
        );
      });
  };

  const handleApply = () => {
    incrementJobOfferMetric(jobOffer, "intent_apply_count", authenticityToken, true);
    if (jobOffer?.external_application) {
      let url = jobOffer.external_application_url;
      if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
      window.open(url, "_blank", "noopener");
    } else {
      setIsApplying(true);
    }
  };

  // Undo for the "application sent" snackbar: deletes the application that was
  // just created and puts the offer back in its un-applied state.
  const handleUndoApplication = (offerJson, app, snackbarKey) => {
    closeSnackbar(snackbarKey);
    sh.delete(`job_application/${app.id}`, { data: { authenticity_token: authenticityToken } })
      .then(() => {
        const reverted = {
          ...offerJson,
          job_applications: (offerJson.job_applications || []).filter((a) => a.id !== app.id),
        };
        if (updateJobOffer) updateJobOffer(reverted);
        replaceCurrentCandidate(dispatch, authenticityToken, () => {});
        enqueueSnackbar(
          intl.formatMessage({ id: "candidate.job_detail.application_cancelled", defaultMessage: "Candidature annulée" }),
          { variant: "success" }
        );
      })
      .catch(() => {
        enqueueSnackbar(
          intl.formatMessage({ id: "candidate.job_detail.apply_error" }),
          { variant: "error" }
        );
      });
  };

  const showApplicationSentSnackbar = (offerJson, { withUndo = false } = {}) => {
    const app = (offerJson?.job_applications || []).find(
      (a) => String(a.candidate_id) === String(currentCandidate?.id)
    );
    enqueueSnackbar(
      intl.formatMessage({ id: "applicant.application_submitted", defaultMessage: "Candidature envoyée" }),
      {
        variant: "success",
        autoHideDuration: 6000,
        action: (key) => (
          <>
            {withUndo && app && (
              <Box
                component="button"
                onClick={() => handleUndoApplication(offerJson, app, key)}
                sx={snackbarActionSx}
              >
                {intl.formatMessage({ id: "candidate.job_detail.undo", defaultMessage: "Annuler" })}
              </Box>
            )}
            {onNavigate && (
              <Box
                component="button"
                onClick={() => { closeSnackbar(key); onNavigate("applications"); }}
                sx={snackbarActionSx}
              >
                {intl.formatMessage({ id: "candidate.job_detail.track_application", defaultMessage: "Suivre" })}
              </Box>
            )}
          </>
        ),
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!resume) {
      setErrors({ resume: intl.formatMessage({ id: "form_validation.document_required", defaultMessage: "Un document est requis" }) });
      setLoading(false);
      return;
    }

    let resumeUrl = resume;
    try {
      if (resume instanceof File) {
        const presignedFileParams = await uploadResume(resume);
        resumeUrl = presignedFileParams.blob_signed_id;
      }

      let coverLetterDocumentId = null;
      if (coverLetterFile) {
        const clParams = await uploadDocument(coverLetterFile);
        coverLetterDocumentId = clParams.blob_signed_id;
      }

      let documentIds = [];
      if (extraFiles.length > 0) {
        const uploads = await Promise.all(extraFiles.map((f) => uploadDocument(f)));
        documentIds = uploads.map((u) => u.blob_signed_id);
      }

      const res = await sh.post("/job_application", {
        authenticity_token: authenticityToken,
        job_offer_id: jobOffer.id,
        cover_letter: coverLetter,
        cover_letter_document: coverLetterDocumentId,
        resume_url: resumeUrl,
        documents: documentIds,
        ...getUtmParams(),
      });

      if (railsEnv === "production") trackJobOfferApplication();
      if (updateJobOffer) updateJobOffer(res.data);
      setLoading(false);
      setIsApplying(false);
      // Applying saves the uploaded CV on the profile, which can bump the
      // completion percentage. The user already gets the "application sent"
      // snackbar, so don't stack a profile boost one on top of it.
      suppressNextProfileBoost();
      replaceCurrentCandidate(dispatch, authenticityToken, () => {});
      showApplicationSentSnackbar(res.data);
    } catch (err) {
      console.error(err);
      setLoading(false);
      const message = currentCandidate?.whitelisted
        ? "candidate.job_detail.apply_error"
        : "settings.must_be_whitelisted";
      enqueueSnackbar(intl.formatMessage({ id: message }), { variant: "error" });
    }
  };

  const locationLabels = formatJobOfferLocations(jobOffer?.locations || []);

  const getJobStatusLabel = () => {
    const status = Array.isArray(jobOffer?.job_statuses) && jobOffer.job_statuses.length > 0
      ? jobOffer.job_statuses[0] : null;
    if (!status) return null;
    return intl.formatMessage({ id: `candidate.status_map.${status}`, defaultMessage: status });
  };

  const descriptionHtml = jobOffer?.description;
  const responsibilitiesHtml = jobOffer?.responsibilities;
  const profileHtml = jobOffer?.profile;
  const offerHtml = jobOffer?.offer;

  const advantages = jobOffer?.offer_advantages;
  const hasAdvantages = advantages && (
    advantages.advantages?.length > 0 ||
    advantages.schedules?.length > 0 ||
    advantages.environments?.length > 0 ||
    advantages.primes?.length > 0
  );

  if (!jobOffer) return null;

  if (isApplying && !candidateApplication) {
    return (
      <motion.div
        key={`apply-${jobOffer.id}`}
        initial={{ opacity: 0, x: 12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>

          {/* Back link — outside card */}
          <Box
            component="button"
            onClick={() => setIsApplying(false)}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              border: "none", bgcolor: "transparent", cursor: "pointer",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: "12px", color: "hsl(178, 10%, 45%)", p: 0,
              "&:hover": { color: "hsl(178, 93%, 10%)" },
              transition: "color 0.15s",
            }}
          >
            <ArrowLeft size={14} />
            {intl.formatMessage({ id: "candidate.job_detail.back_to_offer" })}
          </Box>

          <Box sx={{
            display: "flex", alignItems: "center", gap: "12px",
            ...cardSx, p: "12px 16px",
          }}>
            <ProfilePicture
              picture={company?.logo_url}
              picturesmall={company?.small_logo_url}
              size={36}
              initials={company?.name?.[0]}
              alt={`Logo ${company?.name}`}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                fontSize: "14px", color: "hsl(178, 93%, 10%)",
                letterSpacing: "-0.01em",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {jobOffer.job_title}
              </Typography>
              <Typography sx={{
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                fontSize: "12px", color: "hsl(178, 10%, 45%)",
              }}>
                {company?.name}{locationLabels.length > 0 ? ` · ${locationLabels[0]}` : ""}
              </Typography>
            </Box>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>

            {/* CV upload */}
            <Box sx={{ ...cardSx, display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>
                {intl.formatMessage({ id: "candidate.job_detail.cv" })} <Box component="span" sx={{ color: "hsl(0, 65%, 51%)" }}>*</Box>
              </Typography>
              <FileDropzone
                height="80px"
                handleFileInput={([file]) => {
                  if (!file) return;
                  setResume(file);
                  setErrors((p) => ({ ...p, resume: "" }));
                }}
                filesLimit={1}
                initialFiles={initialFiles}
                deleteJustImportedFile={() => setResume(undefined)}
                fileName={`${currentCandidate?.first_name} ${currentCandidate?.last_name} - CV.pdf`}
                type="candidate"
              />
              {errors?.resume && (
                <Typography sx={{ fontSize: "12px", color: "hsl(0, 65%, 51%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {errors.resume}
                </Typography>
              )}
            </Box>

            {/* Cover letter */}
            <Box sx={{ ...cardSx, display: "flex", flexDirection: "column", gap: "8px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>
                  {intl.formatMessage({ id: "candidate.job_detail.cover_letter" })}
                </Typography>
                <Box sx={{ display: "flex", gap: "4px" }}>
                  {[
                    { key: "write", icon: <PenLine size={10} />, label: intl.formatMessage({ id: "candidate.job_detail.write" }) },
                    { key: "upload", icon: <Upload size={10} />, label: intl.formatMessage({ id: "candidate.job_detail.upload" }) },
                  ].map((m) => (
                    <Box
                      key={m.key}
                      component="button"
                      type="button"
                      onClick={() => setCoverLetterMode(m.key)}
                      sx={{
                        display: "flex", alignItems: "center", gap: "4px",
                        px: "10px", py: "4px", borderRadius: "8px", border: "none", cursor: "pointer",
                        fontSize: "10px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        bgcolor: coverLetterMode === m.key ? "hsl(178, 93%, 10%)" : "hsla(160, 10%, 96%, 0.5)",
                        color: coverLetterMode === m.key ? "hsl(0, 0%, 100%)" : "hsl(178, 10%, 45%)",
                        transition: "all 0.15s",
                      }}
                    >
                      {m.icon} {m.label}
                    </Box>
                  ))}
                </Box>
              </Box>
              {coverLetterMode === "write" ? (
                <Box
                  component="textarea"
                  value={coverLetter}
                  onChange={(e) => setCoverLetter(e.target.value)}
                  placeholder={intl.formatMessage({ id: "candidate.job_detail.cover_letter_placeholder" })}
                  rows={5}
                  sx={{
                    width: "100%", border: "1px solid hsla(160, 10%, 90%, 0.4)",
                    borderRadius: "12px", p: "12px", resize: "none",
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    fontSize: "14px", color: "hsl(178, 93%, 10%)",
                    bgcolor: "hsla(160, 10%, 96%, 0.3)",
                    outline: "none", minHeight: "128px",
                    "&:focus": { borderColor: "hsla(153, 68%, 25%, 0.4)" },
                    "&::placeholder": { color: "hsl(178, 10%, 45%)", opacity: 0.5 },
                    transition: "border-color 0.15s",
                    boxSizing: "border-box",
                  }}
                />
              ) : (
                <>
                  {coverLetterFile ? (
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: "12px",
                      p: "12px", borderRadius: "12px",
                      bgcolor: "hsla(160, 10%, 96%, 0.3)", border: "1px solid hsla(160, 10%, 90%, 0.4)",
                    }}>
                      <FileText size={16} color="hsl(153, 68%, 25%)" style={{ flexShrink: 0 }} />
                      <Typography sx={{
                        flex: 1, fontSize: "12px", color: "hsl(178, 93%, 10%)",
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {coverLetterFile.name}
                      </Typography>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setCoverLetterFile(null)}
                        sx={{
                          border: "none", bgcolor: "transparent", cursor: "pointer", p: 0,
                          color: "hsl(178, 10%, 45%)", display: "flex",
                          "&:hover": { color: "hsl(0, 65%, 51%)" },
                          transition: "color 0.15s",
                        }}
                      >
                        <Trash2 size={14} />
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => clInputRef.current?.click()}
                      sx={{
                        width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                        p: "16px", borderRadius: "12px",
                        border: "2px dashed hsla(160, 10%, 90%, 0.6)",
                        bgcolor: "transparent", color: "hsl(178, 10%, 45%)", cursor: "pointer",
                        "&:hover": { borderColor: "hsla(153, 68%, 25%, 0.4)", color: "hsl(153, 68%, 25%)" },
                        transition: "all 0.15s",
                      }}
                    >
                      <Upload size={16} />
                      <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                        {intl.formatMessage({ id: "candidate.job_detail.upload_letter" })}
                      </Typography>
                    </Box>
                  )}
                  <input
                    ref={clInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setCoverLetterFile(file);
                      e.target.value = "";
                    }}
                  />
                </>
              )}
            </Box>

            {/* Extra documents */}
            <Box sx={{ ...cardSx, display: "flex", flexDirection: "column", gap: "8px" }}>
              <Box
                component="label"
                sx={{
                  display: "flex", alignItems: "center", gap: "8px", cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <Box
                  component="input"
                  type="checkbox"
                  checked={showExtraDocs}
                  onChange={(e) => {
                    setShowExtraDocs(e.target.checked);
                    if (!e.target.checked) setExtraFiles([]);
                  }}
                  sx={{
                    width: "16px", height: "16px", cursor: "pointer",
                    accentColor: "hsl(153, 68%, 25%)",
                  }}
                />
                <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>
                  {intl.formatMessage({ id: "candidate.job_detail.extra_documents" })}
                </Typography>
              </Box>
              {showExtraDocs && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {extraFiles.map((f, i) => (
                    <Box key={i} sx={{
                      display: "flex", alignItems: "center", gap: "12px",
                      p: "10px", borderRadius: "12px",
                      bgcolor: "hsla(160, 10%, 96%, 0.3)", border: "1px solid hsla(160, 10%, 90%, 0.4)",
                    }}>
                      <Paperclip size={14} color="hsl(178, 10%, 45%)" style={{ flexShrink: 0 }} />
                      <Typography sx={{
                        flex: 1, fontSize: "12px", color: "hsl(178, 93%, 10%)",
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {f.name}
                      </Typography>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setExtraFiles((prev) => prev.filter((_, j) => j !== i))}
                        sx={{
                          border: "none", bgcolor: "transparent", cursor: "pointer", p: 0,
                          color: "hsl(178, 10%, 45%)", display: "flex",
                          "&:hover": { color: "hsl(0, 65%, 51%)" },
                          transition: "color 0.15s",
                        }}
                      >
                        <Trash2 size={13} />
                      </Box>
                    </Box>
                  ))}
                  {extraFiles.length < 5 && (
                    <FileDropzone
                      key={extraFiles.length}
                      height="64px"
                      handleFileInput={(files) => setExtraFiles((prev) => [...prev, ...files])}
                      filesLimit={1}
                      initialFiles={[]}
                      deleteJustImportedFile={() => {}}
                      accept={EXTRA_DOCUMENT_ACCEPT}
                      fileTypesLabel={intl.formatMessage({ id: "devise.signup.drop_document_types" })}
                      type="document"
                    />
                  )}
                </Box>
              )}
            </Box>

            {/* Submit */}
            <Box
              component="button"
              type="submit"
              disabled={loading}
              sx={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                py: "14px", borderRadius: "12px", border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.4 : 1,
                bgcolor: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)",
                fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px",
                "&:hover": { bgcolor: loading ? "hsl(153, 68%, 25%)" : "hsl(153, 68%, 30%)" },
                transition: "all 0.15s",
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={16} sx={{ color: "hsl(0, 0%, 100%)" }} />
                  <FormattedMessage id="job_search.applying" defaultMessage="Envoi en cours..." />
                </>
              ) : (
                <>
                  <Send size={14} />
                  {intl.formatMessage({ id: "candidate.job_detail.submit_application" })}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </motion.div>
    );
  }

  return (
    <motion.div
      key={jobOffer.id}
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", pb: { xs: "88px", sm: 0 } }}>

        {/* Header card */}
        <Box sx={cardSx}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px", mb: "4px" }}>
            <ProfilePicture
              picture={company?.logo_url}
              picturesmall={company?.small_logo_url}
              size={56}
              initials={company?.name?.[0]}
              alt={`Logo ${company?.name}`}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                fontSize: "18px",
                color: "hsl(178, 93%, 10%)",
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {jobOffer.job_title}
              </Typography>
              <Typography sx={{
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                fontSize: "14px",
                color: "hsl(178, 10%, 45%)",
                mt: "2px",
              }}>
                {company?.name}
              </Typography>
            </Box>
          </Box>

          {/* Info pills */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mt: "16px", mb: "16px" }}>
            {locationLabels.length > 0 && (
              <Box sx={infoPillSx}>
                <MapPin size={12} color="hsl(178, 10%, 45%)" />
                {locationLabels.join(" · ")}
              </Box>
            )}
            {getJobStatusLabel() && (
              <Box sx={infoPillSx}>
                <Clock size={12} color="hsl(178, 10%, 45%)" />
                {getJobStatusLabel()}
              </Box>
            )}
            {jobOffer.start_date && (
              <Box sx={infoPillSx}>
                <Calendar size={12} color="hsl(178, 10%, 45%)" />
                {intl.formatMessage({ id: "job_offer.start_date", defaultMessage: "Début" })} : {new Date(jobOffer.start_date).toLocaleDateString(dateLocale)}
              </Box>
            )}
            {jobOffer.monthly_offer && (
              <Box sx={infoPillSx}>
                <Euro size={12} color="hsl(178, 10%, 45%)" />
                {jobOffer.monthly_offer}
              </Box>
            )}
          </Box>

          {/* Category chips */}
          {jobOffer.categories?.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {jobOffer.categories.map((cat, i) => (
                <Chip
                  key={i}
                  label={intl.formatMessage({ id: `categories.${cat}`, defaultMessage: cat.replace(/-/g, " ") })}
                  size="small"
                  sx={{
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    fontSize: "12px",
                    height: "24px",
                    borderRadius: "20px",
                    bgcolor: "hsl(178, 93%, 10%)",
                    color: "hsl(0, 0%, 100%)",
                    "& .MuiChip-label": { padding: "0 8px" },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Actions — on mobile this is a fixed bottom bar so Apply stays one
            thumb-tap away while reading the offer (panel is only rendered
            inside the full-screen detail page on mobile) */}
        <Box sx={{
          display: "flex", gap: "8px",
          position: { xs: "fixed", sm: "static" },
          bottom: { xs: 0, sm: "auto" },
          left: { xs: 0, sm: "auto" },
          right: { xs: 0, sm: "auto" },
          zIndex: { xs: 20, sm: "auto" },
          bgcolor: { xs: "hsl(0, 0%, 100%)", sm: "transparent" },
          borderTop: { xs: "1px solid hsl(160, 10%, 90%)", sm: "none" },
          p: { xs: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))", sm: 0 },
        }}>
          {candidateApplication ? (
            <Box sx={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              py: "12px", borderRadius: "12px",
              bgcolor: "hsla(153, 68%, 25%, 0.1)", color: "hsl(153, 68%, 25%)",
              fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px",
            }}>
              <CheckCircle size={16} />
              {intl.formatMessage({ id: "candidate.job_detail.application_sent" })}
            </Box>
          ) : jobOffer.external_application ? (
            <Box
              component="button"
              onClick={handleApply}
              sx={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                py: "12px", borderRadius: "12px", border: "none", cursor: "pointer",
                bgcolor: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)",
                fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px",
                "&:hover": { bgcolor: "hsl(153, 68%, 30%)" },
                transition: "background 0.15s",
              }}
            >
              <ExternalLink size={14} />
              {intl.formatMessage({ id: "candidate.job_detail.apply_external", defaultMessage: "Postuler sur le site" })}
            </Box>
          ) : currentCandidate?.resume_url ? (
            <>
              <Box
                component="button"
                onClick={async () => {
                  incrementJobOfferMetric(jobOffer, "intent_apply_count", authenticityToken, true);
                  setLoading(true);
                  try {
                    const res = await sh.post("/job_application", {
                      authenticity_token: authenticityToken,
                      job_offer_id: jobOffer.id,
                      resume_url: currentCandidate.resume_url,
                      ...getUtmParams(),
                    });
                    if (railsEnv === "production") trackJobOfferApplication();
                    if (updateJobOffer) updateJobOffer(res.data);
                    replaceCurrentCandidate(dispatch, authenticityToken, () => {});
                    showApplicationSentSnackbar(res.data, { withUndo: true });
                  } catch {
                    const message = currentCandidate?.whitelisted ? "candidate.job_detail.apply_error" : "settings.must_be_whitelisted";
                    enqueueSnackbar(intl.formatMessage({ id: message }), { variant: "error" });
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                sx={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  py: "12px", borderRadius: "12px", border: "none", cursor: loading ? "not-allowed" : "pointer",
                  bgcolor: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)",
                  fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px",
                  opacity: loading ? 0.7 : 1,
                  "&:hover": { bgcolor: "hsl(153, 68%, 30%)" },
                  transition: "background 0.15s",
                }}
              >
                {loading ? <CircularProgress size={14} sx={{ color: "hsl(0,0%,100%)" }} /> : <Send size={14} />}
                {intl.formatMessage({ id: "candidate.job_detail.apply_one_click" })}
              </Box>
              <Box
                component="button"
                onClick={handleApply}
                sx={{
                  flex: { xs: 1, sm: "none" },
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  px: { xs: 0, sm: "20px" }, py: "12px", borderRadius: "12px", cursor: "pointer",
                  border: "1px solid hsl(160, 10%, 90%)",
                  bgcolor: "hsl(0, 0%, 100%)", color: "hsl(178, 93%, 10%)",
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: "14px",
                  "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.5)" },
                  transition: "background 0.15s",
                }}
              >
                <FileText size={14} />
                {intl.formatMessage({ id: "candidate.job_detail.apply" })}
              </Box>
            </>
          ) : (
            <Box
              component="button"
              onClick={handleApply}
              sx={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                py: "12px", borderRadius: "12px", border: "none", cursor: "pointer",
                bgcolor: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)",
                fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px",
                "&:hover": { bgcolor: "hsl(153, 68%, 30%)" },
                transition: "background 0.15s",
              }}
            >
              <Send size={14} />
              {intl.formatMessage({ id: "candidate.job_detail.apply" })}
            </Box>
          )}
          {!candidateApplication && (
            <Box
              component="button"
              onClick={handleFavorite}
              sx={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: "48px", borderRadius: "12px", border: "1px solid hsl(160, 10%, 90%)", cursor: "pointer",
                bgcolor: "hsl(0, 0%, 100%)",
                color: isFavorite ? "hsl(153, 68%, 25%)" : "hsl(178, 10%, 45%)",
                "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.5)" },
                transition: "all 0.15s",
              }}
            >
              <Bookmark
                size={16}
                color={isFavorite ? "hsl(153, 68%, 25%)" : "hsl(178, 10%, 45%)"}
                fill={isFavorite ? "currentColor" : "none"}
              />
            </Box>
          )}
        </Box>

        {/* Description */}
        {descriptionHtml && (
          <Box sx={cardSx}>
            <Typography sx={sectionTitleSx}>{intl.formatMessage({ id: "candidate.job_detail.description_title" })}</Typography>
            <Box sx={{
              "& p": { margin: "0 0 8px 0", lineHeight: 1.6 },
              "& ul, & ol": { margin: "0 0 8px 0", paddingLeft: "20px" },
              "& li": { marginBottom: "4px" },
              fontSize: "14px",
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(178, 10%, 45%)",
              lineHeight: 1.6,
            }}>
              <HTML html={descriptionHtml} />
            </Box>
          </Box>
        )}

        {/* Responsibilities */}
        {responsibilitiesHtml && (
          <Box sx={cardSx}>
            <Typography sx={sectionTitleSx}>{intl.formatMessage({ id: "candidate.job_detail.responsibilities_title" })}</Typography>
            <Box sx={{
              "& p": { margin: "0 0 8px 0", lineHeight: 1.6 },
              "& ul, & ol": { margin: "0 0 8px 0", paddingLeft: "20px" },
              "& li": { marginBottom: "4px" },
              fontSize: "14px",
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(178, 10%, 45%)",
              lineHeight: 1.6,
            }}>
              <HTML html={responsibilitiesHtml} />
            </Box>
          </Box>
        )}

        {/* Profile */}
        {profileHtml && (
          <Box sx={cardSx}>
            <Typography sx={sectionTitleSx}>{intl.formatMessage({ id: "candidate.job_detail.profile_title" })}</Typography>
            <Box sx={{
              "& p": { margin: "0 0 8px 0", lineHeight: 1.6 },
              "& ul, & ol": { margin: "0 0 8px 0", paddingLeft: "20px" },
              "& li": { marginBottom: "4px" },
              fontSize: "14px",
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(178, 10%, 45%)",
              lineHeight: 1.6,
            }}>
              <HTML html={profileHtml} />
            </Box>
          </Box>
        )}

        {/* Offer details */}
        {offerHtml && (
          <Box sx={cardSx}>
            <Typography sx={sectionTitleSx}>{intl.formatMessage({ id: "candidate.job_detail.offer_title" })}</Typography>
            <Box sx={{
              "& p": { margin: "0 0 8px 0", lineHeight: 1.6 },
              "& ul, & ol": { margin: "0 0 8px 0", paddingLeft: "20px" },
              "& li": { marginBottom: "4px" },
              fontSize: "14px",
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(178, 10%, 45%)",
              lineHeight: 1.6,
            }}>
              <HTML html={offerHtml} />
            </Box>
          </Box>
        )}

        {/* Skills & Languages */}
        {(hardSkills.length > 0 || softSkills.length > 0 || langSkills.length > 0) && (
          <Box sx={cardSx}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {hardSkills.length > 0 && (
                <Box>
                  <Typography sx={{ ...sectionTitleSx, mb: "8px" }}>{intl.formatMessage({ id: "candidate.job_detail.hard_skills" })}</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {hardSkills.map((s) => (
                      <Box key={s.id || s.skill_id} sx={skillChipSx}>
                        {s.name}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {softSkills.length > 0 && (
                <Box>
                  <Typography sx={{ ...sectionTitleSx, mb: "8px" }}>{intl.formatMessage({ id: "candidate.job_detail.soft_skills" })}</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {softSkills.map((s) => (
                      <Box key={s.id || s.skill_id} sx={skillChipSx}>
                        {s.name}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {langSkills.length > 0 && (
                <Box>
                  <Typography sx={{ ...sectionTitleSx, mb: "8px" }}>{intl.formatMessage({ id: "candidate.job_detail.languages" })}</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {langSkills.map((s) => (
                      <Box key={s.id || s.skill_id} sx={skillChipSx}>
                        {s.name}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Advantages */}
        {hasAdvantages && (
          <Box sx={cardSx}>
            <Typography sx={sectionTitleSx}>{intl.formatMessage({ id: "candidate.job_detail.advantages" })}</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {advantages.advantages?.map((adv, i) => (
                <Box key={`adv-${i}`} sx={{
                  ...skillChipSx,
                  bgcolor: "hsl(178, 93%, 10%)",
                  color: "hsl(0, 0%, 100%)",
                  border: "none",
                }}>
                  {intl.formatMessage({ id: `offer_advantages.advantages.${adv}`, defaultMessage: adv.replace(/_/g, " ") })}
                </Box>
              ))}
              {advantages.schedules?.map((s, i) => (
                <Box key={`sched-${i}`} sx={{
                  ...skillChipSx,
                  bgcolor: "transparent",
                  color: "hsl(178, 93%, 10%)",
                  border: "1px solid hsla(178, 93%, 10%, 0.3)",
                }}>
                  {intl.formatMessage({ id: `offer_advantages.schedules.${s}`, defaultMessage: s.replace(/_/g, " ") })}
                </Box>
              ))}
              {advantages.environments?.map((env, i) => (
                <Box key={`env-${i}`} sx={{
                  ...skillChipSx,
                  bgcolor: "transparent",
                  color: "hsl(178, 93%, 10%)",
                  border: "1px solid hsla(178, 93%, 10%, 0.3)",
                }}>
                  {intl.formatMessage({ id: `offer_advantages.environments.${env}`, defaultMessage: env.replace(/_/g, " ") })}
                </Box>
              ))}
              {advantages.primes?.map((p, i) => (
                <Box key={`prime-${i}`} sx={{
                  ...skillChipSx,
                  bgcolor: "hsla(153, 68%, 25%, 0.1)",
                  color: "hsl(153, 68%, 25%)",
                  border: "none",
                }}>
                  {intl.formatMessage({ id: `offer_advantages.primes.${p}`, defaultMessage: p.replace(/_/g, " ") })}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </motion.div>
  );
};

export default JobDetailPanel;
